import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { userAgentPrefsApi, type AgentPrefs } from "@/api/user-agent-prefs";

const QK = ["me", "agent-prefs"] as const;

export function useAgentPrefs() {
  return useQuery({
    queryKey: QK,
    queryFn: userAgentPrefsApi.get,
    staleTime: 30_000,
  });
}

/**
 * The agent to show when the URL names none: the user's last-used agent if
 * they can still see it, else the first one. The sidebar's highlight and the
 * chat's own pick must agree, so both use this; the chat used to take the
 * first agent while the sidebar showed the last-used one (2026-10-09).
 */
export function pickDefaultAgentId(
  agents: ReadonlyArray<{ id: string }>,
  lastUsedAgentId: string | null | undefined,
): string | null {
  if (agents.length === 0) return null;
  return lastUsedAgentId && agents.some((a) => a.id === lastUsedAgentId)
    ? lastUsedAgentId
    : agents[0]!.id;
}

export function useTouchAgent() {
  // Genuinely fire-and-forget: recording "last used" must never influence
  // what is currently selected.
  //
  // This used to invalidate the prefs query on success, which refetched
  // `lastUsedAgentId` and re-ran the sidebar's agent-resolution — a path
  // from a background mutation back into a URL write, and part of what kept
  // the agent-switcher loop fed. Patch the cached value in place instead so
  // "last used" ordering still updates immediately with no refetch and no
  // feedback edge.
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => userAgentPrefsApi.touch(agentId),
    onSuccess: (_data, agentId) => {
      const previous = qc.getQueryData<AgentPrefs>(QK);
      if (previous) qc.setQueryData<AgentPrefs>(QK, { ...previous, lastUsedAgentId: agentId });
    },
  });
}

export function useSetAgentPinned() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, pinned }: { agentId: string; pinned: boolean }) =>
      userAgentPrefsApi.setPinned(agentId, pinned),
    onMutate: async ({ agentId, pinned }) => {
      // Optimistic update — the sidebar's pinned section repaints
      // instantly. The fetched response then reconciles ordering.
      await qc.cancelQueries({ queryKey: QK });
      const previous = qc.getQueryData<AgentPrefs>(QK);
      if (previous) {
        const next: AgentPrefs = pinned
          ? {
              ...previous,
              pinnedAgentIds: previous.pinnedAgentIds.includes(agentId)
                ? previous.pinnedAgentIds
                : [agentId, ...previous.pinnedAgentIds],
            }
          : {
              ...previous,
              pinnedAgentIds: previous.pinnedAgentIds.filter((id) => id !== agentId),
            };
        qc.setQueryData(QK, next);
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(QK, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QK }),
  });
}
